import React, { useEffect, useState } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { archonRoutes } from './archon/routes';
import { AppErrorElement, WorkspaceErrorElement } from './components/errors/RouteErrorElement';
import { ControlLayout } from './components/layout/ControlLayout';
import { RouteHydrateFallback } from './components/navigation/RouteNavigationProgress';
import { useAuthContext } from './context/AuthContext';
import { evalRoutes } from './eval/routes';
import { ForgeLayout } from './forge/components/layout/ForgeLayout';
import {
  FORGE_CLEANING_TOOL_PATH,
  FORGE_DISTRACTORS_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
  FORGE_SYSTEM_HEALTH_PATH,
  FORGE_UNRESOLVED_TASK_LIST_PATH,
} from './forge/paths';
import { forgeRoutes } from './forge/routes';
import { fetchRecognitionReviewAccess } from './forge/services/ForgeApiService';
import { loadRouteChunk } from './forge/utils/lazyRouteModule';
import { DocumentTitleProvider } from './navigation/documentTitle';

export function recognitionLandingPath(
  hasInternalAccess: boolean,
  hasRecognitionAccess: boolean,
  internalPath: string
): string {
  if (hasInternalAccess) return internalPath;
  return hasRecognitionAccess ? FORGE_RECOGNITION_OKRS_PATH : '/embeddings/detail';
}

const RecognitionAwareRedirect: React.FC<{ internalPath: string }> = ({ internalPath }) => {
  const { token, hasInternalAccess } = useAuthContext();
  const [recognitionPath, setRecognitionPath] = useState<string | null>(null);

  useEffect(() => {
    if (hasInternalAccess || !token) return undefined;
    let active = true;
    setRecognitionPath(null);
    void fetchRecognitionReviewAccess(token)
      .then((response) => {
        if (active) {
          setRecognitionPath(recognitionLandingPath(false, response.hasAccess, internalPath));
        }
      })
      .catch(() => {
        if (active) setRecognitionPath(recognitionLandingPath(false, false, internalPath));
      });
    return () => {
      active = false;
    };
  }, [hasInternalAccess, internalPath, token]);

  if (hasInternalAccess) {
    return <Navigate to={recognitionLandingPath(true, false, internalPath)} replace />;
  }
  if (!token) return <Navigate to="/embeddings/detail" replace />;
  return recognitionPath ? <Navigate to={recognitionPath} replace /> : null;
};

const RootRedirect: React.FC = () => <RecognitionAwareRedirect internalPath="/stores" />;

// Sits above every route so one writer names the browser tab, redirects and the
// error page included. It renders no chrome of its own.
const RootShell: React.FC = () => (
  <DocumentTitleProvider>
    <Outlet />
  </DocumentTitleProvider>
);

const CleaningLegacyRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`${FORGE_CLEANING_TOOL_PATH}${location.search}`} replace />;
};

type RouteCapability =
  | 'hasInternalAccess'
  | 'hasRemoteDataCollectionAccess'
  | 'hasPogRulesReadAccess';

const ProtectedRoute: React.FC<{
  element: React.ReactElement;
  capability?: RouteCapability;
}> = ({ element, capability = 'hasInternalAccess' }) => {
  const auth = useAuthContext();

  if (!auth[capability]) {
    return <Navigate to="/embeddings/detail" replace />;
  }

  return element;
};

// Control's error element sends people to `/`, which is the capability-aware landing
// redirect: Control itself has no sidebar to navigate with.
const controlErrorElement = <WorkspaceErrorElement homePath="/" homeLabel="Go to start" />;
const forgeErrorElement = (
  <WorkspaceErrorElement homePath={FORGE_SYSTEM_HEALTH_PATH} homeLabel="Back to system health" />
);

const router = createBrowserRouter([
  {
    // Pathless root. It exists only to own the application-level `errorElement`, which is
    // the last line of defence: a failure in a layout element itself lands here, because
    // the per-workspace boundaries sit *inside* those layouts. Without it react-router
    // falls back to its own stack-trace page and the app looks blank.
    errorElement: <AppErrorElement />,
    element: <RootShell />,
    // Covers the initial-hydration window. When the first match carries `lazy` — any
    // cold deep link now that the pages are split — react-router renders nothing at all
    // until the chunk lands, so without this a direct link painted a blank document.
    HydrateFallback: RouteHydrateFallback,
    children: [
      {
        path: '/',
        element: <RootRedirect />,
      },
      {
        // Control has no shell of its own, so this pathless route is what puts every Control page
        // inside the token scope. It adds no path segment and no chrome.
        element: <ControlLayout />,
        children: [
          {
            errorElement: controlErrorElement,
            children: [
              {
                // Every Control page behind the internal-access gate shares one guard route
                // rather than repeating the wrapper per page, so the pages below can each be
                // a `lazy` chunk. The guard renders `<Outlet />`, so it still runs before the
                // matched child renders.
                element: <ProtectedRoute element={<Outlet />} />,
                children: [
                  {
                    path: '/stores',
                    lazy: async () => ({
                      Component: (await loadRouteChunk(() => import('./pages/stores/StoresPage')))
                        .default,
                    }),
                  },
                  {
                    path: '/stores/:storeName/mod-eligibility',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(
                          () => import('./pages/mod-eligibility/ModEligibilityPage')
                        )
                      ).ModEligibilityPage,
                    }),
                  },
                  {
                    path: '/chains/:chainName/pog-rules',
                    // Guarded again inside the Control block: POG rules need `pog-rules:r` on top
                    // of Control access, so a blanket `control:*` grant lands on the redirect
                    // rather than on a page whose every call would 403.
                    element: (
                      <ProtectedRoute element={<Outlet />} capability="hasPogRulesReadAccess" />
                    ),
                    children: [
                      {
                        index: true,
                        lazy: async () => ({
                          Component: (
                            await loadRouteChunk(() => import('./pages/pog-rules/PogRulesPage'))
                          ).PogRulesPage,
                        }),
                      },
                    ],
                  },
                  {
                    path: '/pog-reset',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./pages/pog-reset/PogResetPage'))
                      ).PogResetPage,
                    }),
                  },
                  {
                    path: '/stores/:storeName/pog-reset',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./pages/pog-reset/PogResetPage'))
                      ).PogResetPage,
                    }),
                  },
                  {
                    // Before :taskId so 'mods' isn't captured as a task id.
                    path: '/stores/:storeName/pog-reset/mods',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./pages/pog-reset/PogResetModsPage'))
                      ).PogResetModsPage,
                    }),
                  },
                  {
                    path: '/stores/:storeName/pog-reset/install-map',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(
                          () => import('./pages/pog-reset/PogResetInstallMapPage')
                        )
                      ).PogResetInstallMapPage,
                    }),
                  },
                  {
                    path: '/stores/:storeName/pog-reset/stats',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./pages/pog-reset/PogResetStatsPage'))
                      ).PogResetStatsPage,
                    }),
                  },
                  {
                    path: '/stores/:storeName/pog-reset/:taskId',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(
                          () => import('./pages/pog-reset/PogResetTaskDetailPage')
                        )
                      ).PogResetTaskDetailPage,
                    }),
                  },
                  {
                    // Before :deviceName? so 'aisle' isn't captured as a device name.
                    path: '/stores/:storeName/aisle',
                    lazy: async () => ({
                      Component: (await loadRouteChunk(() => import('./pages/aisle/AislePage')))
                        .AislePage,
                    }),
                  },
                  {
                    path: '/stores/:storeName/:deviceName?',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./pages/store-details/StoreDetailsPage'))
                      ).StoreDetailsPage,
                    }),
                  },
                  {
                    path: '/embeddings',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./pages/embeddings/EmbeddingsPage'))
                      ).EmbeddingsPage,
                    }),
                  },
                  {
                    path: '/tasks',
                    lazy: async () => ({
                      Component: (await loadRouteChunk(() => import('./pages/tasks/TasksPage')))
                        .TasksPage,
                    }),
                  },
                  {
                    path: '/tasks/:taskId',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./pages/tasks/TaskDetailPageWrapper'))
                      ).TaskAuditPageWrapper,
                    }),
                  },
                  {
                    path: '/gt/audit/:gtSampleId',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(
                          () => import('./pages/ground-truth/GroundTruthAuditPage')
                        )
                      ).GroundTruthAuditPage,
                    }),
                  },
                  {
                    path: '/images/:imageId/audit',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./pages/tasks/ImageAuditPageWrapper'))
                      ).ImageAuditPageWrapper,
                    }),
                  },
                  {
                    path: '/embeddings/candidates',
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./pages/embeddings/CandidatesPage'))
                      ).CandidatesPage,
                    }),
                  },
                ],
              },
              // Ungated Control pages: these are the ones a non-internal user is redirected
              // *to*, so they must stay reachable without the capability.
              {
                path: '/embeddings/detail',
                lazy: async () => ({
                  Component: (
                    await loadRouteChunk(() => import('./pages/embeddings/EmbeddingDetailPage'))
                  ).default,
                }),
              },
              {
                path: '/embeddings/candidates/:imageId',
                lazy: async () => ({
                  Component: (
                    await loadRouteChunk(() => import('./pages/embeddings/CandidateDetailPage'))
                  ).CandidateDetailPage,
                }),
              },
              {
                path: '/outs-selection/:imageId',
                lazy: async () => ({
                  Component: (
                    await loadRouteChunk(() => import('./pages/embeddings/OutsSelectionPage'))
                  ).OutsSelectionPage,
                }),
              },
            ],
          },
        ],
      },
      {
        path: '/forge',
        element: <RecognitionAwareRedirect internalPath={FORGE_SYSTEM_HEALTH_PATH} />,
      },
      {
        path: '/forge/labeling-tools/master-label',
        element: <CleaningLegacyRedirect />,
      },
      {
        element: <ForgeLayout />,
        children: [
          {
            errorElement: forgeErrorElement,
            children: [
              {
                path: FORGE_CLEANING_TOOL_PATH,
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./forge/pages/admin/CleaningToolPage'))
                      ).CleaningToolPage,
                    }),
                  },
                ],
              },
              {
                path: FORGE_REMOTE_DC_TOOL_PATH,
                element: (
                  <ProtectedRoute capability="hasRemoteDataCollectionAccess" element={<Outlet />} />
                ),
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(
                          () => import('./forge/pages/admin/MasterDataCollectionPage')
                        )
                      ).MasterDataCollectionPage,
                    }),
                  },
                ],
              },
              {
                path: FORGE_DISTRACTORS_PATH,
                element: (
                  <ProtectedRoute capability="hasRemoteDataCollectionAccess" element={<Outlet />} />
                ),
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(() => import('./forge/pages/admin/DistractorsPage'))
                      ).DistractorsPage,
                    }),
                  },
                ],
              },
              {
                path: FORGE_REMOTE_DC_TASK_LIST_PATH,
                element: (
                  <ProtectedRoute capability="hasRemoteDataCollectionAccess" element={<Outlet />} />
                ),
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(
                          () => import('./forge/pages/dashboard/RemoteDataCollectionDashboard')
                        )
                      ).RemoteDataCollectionDashboard,
                    }),
                  },
                ],
              },
              {
                path: FORGE_UNRESOLVED_TASK_LIST_PATH,
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(
                          () => import('./forge/pages/dashboard/UnresolvedTaskListDashboard')
                        )
                      ).UnresolvedTaskListDashboard,
                    }),
                  },
                ],
              },
              {
                path: FORGE_RECOGNITION_OKRS_PATH,
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(
                          () => import('./forge/pages/dashboard/RecognitionOkrsDashboard')
                        )
                      ).RecognitionOkrsDashboard,
                    }),
                  },
                ],
              },
              {
                path: FORGE_PR_REVIEW_PATH,
                children: [
                  {
                    index: true,
                    lazy: async () => ({
                      Component: (
                        await loadRouteChunk(
                          () => import('./forge/pages/admin/RecognitionReviewToolPage')
                        )
                      ).RecognitionReviewToolPage,
                    }),
                  },
                ],
              },
              {
                ...forgeRoutes,
                element: <ProtectedRoute element={<Outlet />} />,
              },
            ],
          },
        ],
      },
      {
        ...evalRoutes,
        element: <ProtectedRoute element={evalRoutes.element as React.ReactElement} />,
      },
      {
        ...archonRoutes,
        element: <ProtectedRoute element={archonRoutes.element as React.ReactElement} />,
      },
    ],
  },
]);

export const App: React.FC = () => {
  return <RouterProvider router={router} />;
};
